import { Component, computed, effect, inject, signal } from "@angular/core";
import { Router } from "@angular/router";
import { FeedbackBaseRequest } from "src/app/core/interfaces/feedback-interface";
import { FeedbackService } from "src/app/features/services/feedback/feedback-service";
import { RandomImageLoaderService } from "src/app/features/services/utils/random-image-loader-service";
import { Button } from "src/app/shared/components/utils/button/button";
import { Icon } from "src/app/shared/components/utils/icon/icon";
import { Modal } from "src/app/shared/components/utils/modal/modal";
import { FeedbackPromptService } from "../../services/utils/feedback-prompt-service";
import { TranslocoModule } from "@jsverse/transloco";
import { UserStore } from "../../store/user-store";
import { UserPermission } from "src/app/core/interfaces/detailed-user-interface";
import { downloadFileFromResponse } from "src/app/shared/utils/download-file";

@Component({
  selector: "app-feedback-page",
  imports: [Button, Icon, Modal, TranslocoModule],
  templateUrl: "./feedback-page.html",
})
export class FeedbackPage {
  private readonly router = inject(Router);
  private readonly randomImageLoaderService = inject(RandomImageLoaderService);
  private readonly _feedbackService = inject(FeedbackService);
  private readonly feedbackPromptService = inject(FeedbackPromptService);

  userStore = inject(UserStore);
  UserPermission = UserPermission;

  image = computed(() => this.randomImageLoaderService.image());

  constructor() {
    const navigation = this.router.currentNavigation();
    const state = navigation?.extras?.state || history.state;
    if (state?.rating) {
      this.rating.set(state.rating);
    }

    effect(() => {
      document.body.style.overflow = this.isModalOpen() ? "hidden" : "";
    });

    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isModalOpen()) this.closeModal();
    });
  }

  rating = signal<number>(0);
  maxRating = signal<number>(5);
  ratingButtons = computed(() =>
    Array.from({ length: this.maxRating() }, (_, i) => i + 1)
  );
  setRating(rating: number): void {
    this.rating.set(rating);
  }

  feedbackNegativeText = signal<string>("");
  feedbackPositiveText = signal<string>("");

  isModalOpen = signal(false);

  openModal() {
    this.isModalOpen.set(true);
  }

  closeModal() {
    this.isModalOpen.set(false);
    this.feedbackPositiveText.set("");
    this.feedbackNegativeText.set("");
    document.body.style.overflow = "";
  }

  completeModal() {
    this.closeModal();
    this.router.navigate(["/user-profile"]);
  }

  onFeedbackNegativeChange(event: Event) {
    const value = (event.target as HTMLTextAreaElement).value;
    this.feedbackNegativeText.set(value);
  }

  onFeedbackPositiveChange(event: Event) {
    const value = (event.target as HTMLTextAreaElement).value;
    this.feedbackPositiveText.set(value);
  }

  submitFeedback() {
    if (this.rating() !== 0) {
      const feedbackBaseRequest: FeedbackBaseRequest = {
        negativeTextValue: this.feedbackNegativeText(),
        positiveTextValue: this.feedbackPositiveText(),
        rating: this.rating(),
      };
      this._feedbackService.postFeedback(feedbackBaseRequest);
      this.openModal();
      this.feedbackPromptService.markFeedbackGiven();
    }
  }

  async downloadFeedbacks() {
    const response = await this._feedbackService.downloadFeedbacks();
    downloadFileFromResponse(response, "feedbacks.csv");
  }
}
